/* eslint-disable import/no-cycle */
/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/require-default-props */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-param-reassign */

import React from 'react';
import {
  Box,
  FormControl,
  FormErrorMessage,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  theme,
} from '@chakra-ui/core';
import isString from 'lodash/isString';
import { useTranslation } from 'react-i18next';
import { useGetItemProperty } from '../hooks/useGetItemProperty';
import useHireFormErrorHandling, { ValidationType, ValidationTypeEnum } from '../hooks/useHireFormErrorHandling';
import { getUniqueSectionName } from '../utils';
import { useUpdateFormItem } from '../hooks/useUpdateFormItem';
import { FORM_ADVANCED_SECTIONS, SECTION_NAMES, ADVANCED_SECTION_TITLES } from '../schemas/formAdvancedSections';
import withToggleVisibility from '../utils/withToggleVisibility';
import { TextWithTranslationsT } from '../types';
import { useStoreState, useStoreActions } from '../../../../models/hooks';
import { languages } from '../../hireForm/hireForm.interface';

interface SectionNameInputProps {
  fieldId: string;
  value: string;
  completeValue?: TextWithTranslationsT;
  // path: PathT;
  isAdvanced?: boolean;
  onBlur?: () => void;
  placeholder?: string;
}

function SectionNameInputComponent({
  fieldId: sectionId,
  value: currentFieldValue,
  completeValue,
  onBlur,
  placeholder,
  isAdvanced,
}: SectionNameInputProps) {
  const { t } = useTranslation('hrFormTemplate');
  const handleOnChange = useUpdateFormItem();
  const { currentLanguage, defaultLanguage, formSchema } = useStoreState((state) => state.formBuilder);
  const { setError: setGlobalError, removeError } = useStoreActions((state) => state.formBuilder);

  const [sectionName, setSectionName] = React.useState(currentFieldValue);
  const innerPath = `sectionTitle.${currentLanguage}`;
  const { validate, error, inputRef } = useHireFormErrorHandling({
    targetId: sectionId,
    innerPath,
    validationType: ValidationTypeEnum.SECTION_NAME as ValidationType,
    isAdvanced: isAdvanced || false,
  });

  // const [errorMessage, setErrorMessage] = React.useState<string>();

  // React.useEffect(() => {
  //   setErrorMessage(error);
  // }, [error]);

  React.useEffect(() => {
    validate(currentFieldValue);
    setSectionName(currentFieldValue);
  }, [currentFieldValue]);

  React.useEffect(() => {
    inputRef?.current?.focus();
  }, []);

  const handleSectionNameChange = React.useCallback(
    (newSectionName: string) => {
      validate(newSectionName);
      const { isUnique } = getUniqueSectionName(formSchema, newSectionName);
      const isAdvancedSection = Boolean(FORM_ADVANCED_SECTIONS[newSectionName as keyof typeof FORM_ADVANCED_SECTIONS]);
      if (isAdvancedSection) {
        // setErrorMessage(t('formBuilder.sectionInputAdvancedError'));
        setGlobalError({
          fullPath: innerPath,
          targetId: sectionId,
          innerPath,
          message: t('formBuilder.sectionInputAdvancedError'),
        });
      }
      if (!isUnique) {
        setGlobalError({
          fullPath: innerPath,
          targetId: sectionId,
          innerPath,
          message: t('formBuilder.sectionNameExistsMessage'),
        });
      } else {
        removeError({ targetId: sectionId, innerPath });
      }
      setSectionName(newSectionName);
    },

    [validate, setGlobalError, innerPath, sectionId],
  );

  const handleCommitChange = React.useCallback(
    (value: string) => {
      if (error) return;
      let trimmedValue = value.trim();
      if (trimmedValue !== currentFieldValue) {
        if (trimmedValue) {
          const { section } = getUniqueSectionName(formSchema, trimmedValue);
          trimmedValue = section;
        }
        if (defaultLanguage === currentLanguage) {
          handleOnChange(sectionId, {
            section: trimmedValue,
            sectionTitle: { ...completeValue, [currentLanguage]: trimmedValue },
          });
        } else {
          handleOnChange(sectionId, { sectionTitle: { ...completeValue, [currentLanguage]: trimmedValue } });
        }
      }

      onBlur?.();
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [currentFieldValue, handleOnChange, onBlur, defaultLanguage, currentLanguage, completeValue, sectionId, error],
  );

  return (
    <FormControl isInvalid={Boolean(error)} id={`form-control-section-${sectionId}`}>
      <Input
        ref={inputRef}
        value={sectionName}
        onChange={(e) => handleSectionNameChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Escape') {
            handleCommitChange(e.currentTarget.value);
          }
        }}
        onBlur={(e) => handleCommitChange(e.target.value)}
        size="xs"
        borderRadius="md"
        fontSize="sm"
        borderWidth={1}
        px={2}
        boxShadow="none !important"
        placeholder={placeholder}
      />
      {error && (
        <FormErrorMessage mt={0} fontSize="xs" id={`error-section-name-${sectionId}`}>
          {error}
        </FormErrorMessage>
      )}
    </FormControl>
  );
}

const SectionNameInputComponentWrapper = withToggleVisibility(SectionNameInputComponent);

const PreviewElement = ({
  value: sectionName,
  error,
  placeholderText,
  onClick,
}: {
  value: string;
  error?: string;
  placeholderText?: string;
  onClick?: () => void;
}) => {
  const { t } = useTranslation('hrFormTemplate');
  const hasError = !!error;
  const section = SECTION_NAMES[sectionName as keyof typeof SECTION_NAMES] || sectionName;
  /* eslint-disable-next-line  */
  const renderPlaceholder = !Boolean(section) && Boolean(placeholderText);
  return (
    <Popover placement="bottom-start" isLazy trigger="hover">
      <PopoverTrigger>
        <Box>
          <Text
            onClick={(e) => {
              e.stopPropagation();
              onClick?.();
            }}
            outline="1px solid transparent"
            sx={{
              margin: 0,
              fontWeight: renderPlaceholder ? 'normal' : 'bold',
              cursor: 'text',
              flexGrow: 1,
              fontSize: renderPlaceholder ? 'xs' : 'lg',
              color:
                /* eslint-disable-next-line  */
                hasError && !renderPlaceholder
                  ? theme.colors.red[500]
                  : renderPlaceholder
                  ? theme.colors.gray[400]
                  : theme.colors.gray[600],
              outlineColor: hasError ? theme.colors.red[500] : 'transparent',
              px: hasError ? 2 : 0,
              borderRadius: 'md',
              position: 'relative',
              zIndex: 0,
            }}
            _hover={{
              _after: {
                content: '""',
                position: 'absolute',
                bottom: '-1px',
                left: '-5px',
                width: 'calc(100% + 10px)',
                height: 'calc(100% + 2px)',
                background: '#f2ebff',
                borderRadius: '3px',
                border: '1px solid #e8dbff',
                zIndex: -1,
              },
            }}
          >
            {section || placeholderText}
          </Text>
          {hasError && (
            <Text fontSize="xs" color="red.500" mt={1}>
              {error}
            </Text>
          )}
        </Box>
      </PopoverTrigger>
      <PopoverContent boxShadow="xl">
        <PopoverHeader fontWeight="semibold" fontSize="sm">
          {t('formBuilder.infoTooltips.section.title')}
        </PopoverHeader>
        <PopoverArrow />
        <PopoverBody>
          <Text fontSize="sm">{t('formBuilder.infoTooltips.section.description')}</Text>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export const SectionNameInput = ({ sectionId: fieldId, isAdvanced }: { sectionId: string; isAdvanced?: boolean }) => {
  const currentFieldValue = useGetItemProperty<FORM_ADVANCED_SECTIONS>(fieldId, 'section');
  const sectionTitle = useGetItemProperty<TextWithTranslationsT | undefined>(fieldId, 'sectionTitle');
  const { currentLanguage, availableLanguages } = useStoreState((state) => state.formBuilder);
  const handleOnChange = useUpdateFormItem();

  React.useEffect(() => {
    if (!availableLanguages?.length || !currentFieldValue) return;

    // sectionTitle may be undefined
    const existing = sectionTitle ?? {};

    // choose a fallback value
    const fallbackValue = Object.values(existing)[0] ?? currentFieldValue;

    if (!fallbackValue) return;

    let hasChange = false;

    const nextSectionTitle = languages.reduce<TextWithTranslationsT>(
      (acc, lang) => {
        if (!acc[lang]) {
          acc[lang] = isAdvanced ? ADVANCED_SECTION_TITLES[currentFieldValue][lang] : fallbackValue;
          hasChange = true;
        }
        return acc;
      },
      { ...existing },
    );

    if (hasChange) {
      handleOnChange(fieldId, { sectionTitle: nextSectionTitle });
    }
  }, [sectionTitle, currentFieldValue, fieldId]);

  const selectedLangValue = React.useMemo(() => sectionTitle?.[currentLanguage], [sectionTitle, currentLanguage]);

  return (
    <SectionNameInputComponentWrapper
      initialValue={selectedLangValue || ''}
      completeValue={sectionTitle}
      validate={(cb, value) => {
        if (isString(value)) cb(value);
      }}
      validationType={ValidationTypeEnum.SECTION_NAME}
      fieldId={fieldId}
      innerPath={`sectionTitle.${currentLanguage}`}
      isDisabled={isAdvanced}
      isAdvanced={!!isAdvanced}
      placeholder="Enter section name..."
      previewComponent={(value, error, placeholderText) => {
        return <PreviewElement value={value} error={error} placeholderText={placeholderText} />;
      }}
    />
  );
};
